import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Logo, Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { useSession } from './session'
import { isLocked, loginErrorText } from './model'
import type { AdminAuthApi } from './api'

export interface LoginScreenProps {
  api: AdminAuthApi
}

/**
 * Вход для сотрудников: логин и пароль (В-38). Логин — рабочая почта или
 * телефон; неизвестный логин и неверный пароль дают одно и то же сообщение,
 * чтобы форма не подсказывала, какие учётки существуют.
 *
 * Раскладка прототипа сохранена: слева — что это и как получить доступ,
 * справа — форма. Пустое поле помечается и получает фокус, а о включённом
 * Caps Lock форма предупреждает до того, как пароль не подойдёт.
 */
export function LoginScreen({ api }: LoginScreenProps) {
  const { signIn } = useSession()
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [visible, setVisible] = useState(false)
  const [blank, setBlank] = useState(false)
  const [caps, setCaps] = useState(false)
  const loginRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const submit = useMutation({
    mutationFn: () => api.login(login.trim(), password),
    onSuccess: (result) => {
      signIn(result.tokens, result.mustChangePassword, { login: login.trim(), password })
    },
  })

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!login.trim() || !password) {
      setBlank(true)
      ;(login.trim() ? passwordRef : loginRef).current?.focus()
      return
    }
    setBlank(false)
    submit.mutate()
  }

  function onPasswordKey(event: KeyboardEvent<HTMLInputElement>) {
    setCaps(event.getModifierState('CapsLock'))
  }

  const error = blank ? 'Введите почту или телефон и пароль' : null
  const serverError = submit.error ? loginErrorText(submit.error) : null
  const message = error ?? serverError
  // Блокировка — не опечатка: жёлтым и с тем, как её снять.
  const locked = !error && isLocked(submit.error)
  const loginMissing = blank && !login.trim()
  const passwordMissing = blank && !password

  return (
    <div className="login">
      <div className="lg-wrap">
        <div className="lg-brand">
          <Logo className="lgo" onDark />
          <div>
            <h2>Панель управления сетью аптек</h2>
            <p>
              Заказы, витрина и кэшбэк ведутся здесь, каталог и остатки приходят из FOM. Какие
              разделы откроются, решает роль сотрудника.
            </p>
          </div>
          <section className="lg-steps" aria-labelledby="lg-steps-h">
            <h3 id="lg-steps-h">Как получить доступ</h3>
            <ol>
              <li>
                <span>
                  <b>Учётную запись заводят в разделе «Сотрудники»</b> — это делает супер-админ.
                </span>
              </li>
              <li>
                <span>Он выдаёт временный пароль, роль и аптеку.</span>
              </li>
              <li>
                <span>При первом входе вы смените пароль на свой.</span>
              </li>
            </ol>
          </section>
        </div>

        <form className="lg-form" onSubmit={onSubmit} noValidate>
          <div>
            <h1>Вход для сотрудников</h1>
            <p className="sub">Рабочая почта или телефон и пароль.</p>
          </div>
          {message ? (
            <div className="ui-alert" data-tone={locked ? 'warning' : 'danger'} role="alert">
              <Icon name="i-alert" />
              <div className="txt">
                {locked ? <b>Вход заблокирован</b> : null}
                {message}
              </div>
            </div>
          ) : null}
          <div className="ui-field">
            <label className="ui-label" htmlFor="lg-user">
              Рабочая почта или телефон
            </label>
            <input
              ref={loginRef}
              id="lg-user"
              className="ui-input"
              type="text"
              inputMode="email"
              autoComplete="username"
              placeholder="name@dorixona.uz или +998 90 123-45-67"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={loginMissing || undefined}
              value={login}
              onChange={(e) => {
                setLogin(e.target.value)
              }}
            />
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor="lg-pass">
              Пароль
            </label>
            <div className="lg-pw">
              <input
                ref={passwordRef}
                id="lg-pass"
                className="ui-input"
                type={visible ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Введите пароль"
                aria-invalid={passwordMissing || undefined}
                aria-describedby={caps ? 'lg-caps' : undefined}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                }}
                onKeyDown={onPasswordKey}
                onKeyUp={onPasswordKey}
              />
              <button
                type="button"
                className="ui-btn"
                data-variant="ghost"
                aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
                onClick={() => {
                  setVisible((v) => !v)
                }}
              >
                {visible ? 'Скрыть' : 'Показать'}
              </button>
            </div>
            {caps ? (
              <span className="lg-caps" id="lg-caps">
                <Icon name="i-alert" />
                Включён Caps Lock
              </span>
            ) : null}
          </div>
          <Press
            as="button"
            type="submit"
            className="ui-btn"
            squish="card"
            disabled={submit.isPending}
            aria-busy={submit.isPending}
          >
            {submit.isPending ? 'Входим…' : 'Войти'}
          </Press>
          <p className="lg-foot">
            <Icon name="i-info" />
            <span>
              Забыли пароль — новый задаёт супер-админ в карточке сотрудника. На общем компьютере
              выходите кнопкой «Выйти из системы»: сессия сама не закрывается.
            </span>
          </p>
        </form>
      </div>
    </div>
  )
}
